/**
 * Ball-to-cushion contact (PHY-004).
 *
 * The one thing that matters here, and the reason this is the hardest task in the
 * phase: **the cushion touches the ball above its centre.** The nose sits at about
 * 63.5% of a ball diameter, so contact is ~0.27R above centre rather than level with
 * it. Everything a player knows about rails comes out of that offset — a cushion
 * imparts topspin, and sidespin changes the angle the ball comes back at.
 *
 * Model it as a level contact and English becomes decorative.
 *
 * Follows Mathavan, Jackson & Parkin (2010), integrated as the paper does it (PHY-007);
 * until then it was a single-impulse simplification with two errors in it, see
 * `resolveCushionContact`. Deviations are recorded in docs/physics.md.
 */
import type { PhysicsConfig } from './config.ts';
import type { TableState } from './state.ts';
import type { CushionSegment } from './table.ts';

/**
 * Geometry of the contact, derived once per shot rather than per step.
 *
 * `sinTheta` is how far above centre the nose sits, as a fraction of R; `cosTheta` is
 * therefore how close the ball's CENTRE gets to the nose line horizontally — slightly
 * less than R, because the contact is up the side of the ball rather than on its
 * equator.
 */
export interface CushionContact {
  readonly sinTheta: number;
  readonly cosTheta: number;
  /** Horizontal distance from the nose line to the centre of a touching ball. */
  readonly contactDistanceM: number;
}

export function cushionContact(config: PhysicsConfig): CushionContact {
  // Nose height as a fraction of DIAMETER; the ball's centre sits at one radius.
  const sinTheta = 2 * config.cushionHeightRatio - 1;
  const cosTheta = Math.sqrt(1 - sinTheta * sinTheta);
  return {
    sinTheta,
    cosTheta,
    contactDistanceM: config.ballRadiusM * cosTheta,
  };
}

/**
 * Earliest time within `maxT` at which ball `i` reaches cushion `c`, or -1.
 *
 * Swept, like the ball-ball test: a ball covering half its radius per step would
 * otherwise step straight over a rail.
 */
export function timeToCushion(
  state: TableState,
  i: number,
  cushion: CushionSegment,
  maxT: number,
  contact: CushionContact,
): number {
  const vn = (state.vxMps[i] ?? 0) * cushion.nx + (state.vyMps[i] ?? 0) * cushion.ny;
  if (vn >= 0) return -1; // moving away from this rail, or along it

  const px = (state.xM[i] ?? 0) - cushion.from.xM;
  const py = (state.yM[i] ?? 0) - cushion.from.yM;
  const distance = px * cushion.nx + py * cushion.ny;

  const gap = distance - contact.contactDistanceM;
  const t = gap <= 0 ? 0 : gap / -vn;
  if (t > maxT) return -1;

  /*
   * The contact has to land on the rail itself. Past either end the ball is entering a
   * pocket mouth, where the jaws take over (PHY-005) — so a miss here is correct, not
   * a ball escaping.
   */
  const dirX = cushion.to.xM - cushion.from.xM;
  const dirY = cushion.to.yM - cushion.from.yM;
  const lengthSquared = dirX * dirX + dirY * dirY;
  if (lengthSquared === 0) return -1;

  const hitX = px + (state.vxMps[i] ?? 0) * t;
  const hitY = py + (state.vyMps[i] ?? 0) * t;
  const along = (hitX * dirX + hitY * dirY) / lengthSquared;
  if (along < 0 || along > 1) return -1;

  return t;
}

/**
 * Apply the cushion impact to ball `i`. Returns the approach speed, for the event log.
 *
 * Mathavan, Jackson & Parkin (2010), integrated as the paper does it: in small steps
 * of the normal impulse, with Stronge's energetic restitution deciding when the
 * contact ends (PHY-007). Two contacts act throughout the impact, not one:
 *
 *   • the RAIL, above centre, pushing the ball back and down, with friction;
 *   • the CLOTH, below the ball, which the downward push presses the ball into, with
 *     friction of its own.
 *
 * The cloth's grip during the impact is what kills most of a rolling ball's topspin at
 * the rail. The earlier single-impulse version discarded that downward push (the old
 * deviation D5), and also applied the rail friction's torque with the wrong sign — a
 * rolling ball came off the rail with MORE forward spin than it arrived with, and more
 * energy, and hooked back along the rail (CLAUDE.md §13/L38).
 *
 * Only + − × ÷ and Math.sqrt (ARCHITECTURE.md §5.5); no allocation.
 */
export function resolveCushionContact(
  state: TableState,
  i: number,
  cushion: CushionSegment,
  config: PhysicsConfig,
  contact: CushionContact,
): number {
  const radiusM = config.ballRadiusM;
  const { sinTheta, cosTheta } = contact;

  /*
   * The paper's frame: X along the rail, Y INTO the rail, Z up. `cushion.n` points
   * into the table, so Y = −n, and X = (−ny, nx) makes X × Y = +Z.
   */
  const yX = -cushion.nx;
  const yY = -cushion.ny;
  const xX = -cushion.ny;
  const xY = cushion.nx;

  const vx = state.vxMps[i] ?? 0;
  const vy = state.vyMps[i] ?? 0;
  const wx = state.wxRadS[i] ?? 0;
  const wy = state.wyRadS[i] ?? 0;

  let along = vx * xX + vy * xY;
  let into = vx * yX + vy * yY;
  let spinAlong = wx * xX + wy * xY;
  let spinInto = wx * yX + wy * yY;
  let spinUp = state.wzRadS[i] ?? 0;

  // Normal speed at the contact. A sphere's spin cannot move its surface along the
  // radius, so only the centre's motion counts.
  const approachSpeed = into * cosTheta;
  if (approachSpeed <= 0) return 0; // already leaving the rail

  /*
   * Restitution falls as the impact gets harder — a soft roll comes back off the rail
   * proportionally better than a smashed one. Linear between the two configured
   * values, clamped. Used as the ENERGETIC coefficient, as in the paper.
   */
  const speedFraction = approachSpeed / config.cushionRestitutionSpeedMps;
  const blend = speedFraction > 1 ? 1 : speedFraction;
  const restitution =
    config.cushionRestitutionLow +
    (config.cushionRestitutionHigh - config.cushionRestitutionLow) * blend;

  const muW = config.cushionFriction;
  const muS = config.clothSlidingFriction;
  const steps = config.cushionImpactSteps;
  // Impulse per unit mass per step, sized so a frictionless impact takes `steps`.
  const dp = ((1 + restitution) * into) / steps;
  const spinPerImpulse = 2.5 / radiusM; // Δω per unit impulse-per-mass: 5/(2R)
  /*
   * Friction that would reverse a slip within one step is ramped down in proportion,
   * so a contact that should stick does not chatter from side to side. 3.5 = 1 + 5/2:
   * one unit of friction impulse changes a contact's slip through the centre and
   * through the spin together.
   */
  const railRamp = 3.5 * muW * dp;
  const clothRamp = 3.5 * muS * dp;

  let work = 0;
  let workTarget = 0;
  let compressed = false;

  for (let n = 0; n < steps * 8; n++) {
    // Slip at the rail contact, in its tangent plane (paper eq. 12).
    const railX = along + spinInto * radiusM * sinTheta - spinUp * radiusM * cosTheta;
    const railY = -into * sinTheta + spinAlong * radiusM;
    const railSlip = Math.sqrt(railX * railX + railY * railY);
    // Slip at the cloth, under the ball (eq. 13).
    const clothX = along - spinInto * radiusM;
    const clothY = into + spinAlong * radiusM;
    const clothSlip = Math.sqrt(clothX * clothX + clothY * clothY);

    let railCos = 0;
    let railSin = 0;
    if (railSlip > 0) {
      const scale = (railSlip < railRamp ? railSlip / railRamp : 1) / railSlip;
      railCos = railX * scale;
      railSin = railY * scale;
    }
    let clothCos = 0;
    let clothSin = 0;
    if (clothSlip > 0) {
      const scale = (clothSlip < clothRamp ? clothSlip / clothRamp : 1) / clothSlip;
      clothCos = clothX * scale;
      clothSin = clothY * scale;
    }

    // How hard the ball is pressed into the cloth, per unit rail impulse (eq. 8).
    const clothLoad = sinTheta + muW * railSin * cosTheta;
    const before = into * cosTheta;

    // Paper eq. 14, per unit mass.
    along -= (muW * railCos + muS * clothCos * clothLoad) * dp;
    into -= (cosTheta - muW * sinTheta * railSin + muS * clothSin * clothLoad) * dp;
    spinAlong -= spinPerImpulse * (muW * railSin + muS * clothSin * clothLoad) * dp;
    spinInto -= spinPerImpulse * (muW * railCos * sinTheta - muS * clothCos * clothLoad) * dp;
    spinUp += spinPerImpulse * (muW * railCos * cosTheta) * dp;

    // Work done by the normal force (eq. 16): compression, then restitution.
    work += (dp * (before + into * cosTheta)) / 2;
    if (!compressed && into <= 0) {
      compressed = true;
      workTarget = (1 - restitution * restitution) * work;
    }
    if (compressed && work <= workTarget) break;
  }

  state.vxMps[i] = along * xX + into * yX;
  state.vyMps[i] = along * xY + into * yY;
  state.wxRadS[i] = spinAlong * xX + spinInto * yX;
  state.wyRadS[i] = spinAlong * xY + spinInto * yY;
  state.wzRadS[i] = spinUp;

  return approachSpeed;
}
